import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import './HomePage.css';

export default function HomePage() {
  return (
    <div className="home">
      <Navbar />

      {/* ── Hero header ──────────────────────────────────────────────── */}
      <header className="home-header">
        <div className="home-logo-section">
          <img
            src={`${process.env.PUBLIC_URL}/images/logo.png`}
            alt="Eye2Voice"
            className="home-logo"
          />
        </div>
        <div
          className="home-hero"
          style={{
            '--hero-bg-desktop': `url(${process.env.PUBLIC_URL}/images/hero-banner.png)`,
            '--hero-bg-tablet':  `url(${process.env.PUBLIC_URL}/images/hero-tablet.png)`,
          }}
        >
          <div className="home-hero-content">
            <h1 className="home-hero-heading">
              Communicate Effortlessly<br />
              Anywhere, Anytime on any device
            </h1>
            <p className="home-hero-sub">
              Gaze based assistive communication tool, designed for fast, reliable and easy use.
            </p>
            <a
              href="https://talk.eye2voice.com"
              className="btn-talk-now"
              target="_blank"
              rel="noopener noreferrer"
            >
              Talk Now
            </a>
          </div>
        </div>
      </header>

      <main>

        {/* ── Why We Built ─────────────────────────────────────────────── */}
        <section className="section-why">
          <h2 className="display-heading">Why We Built Eye2Voice</h2>
          <div className="why-cards">
            <div className="why-card why-card--hero">
              <p className="why-hero-text">
                Current AAC gaze-to-speech tools needing specialized hardware that becomes
                outdated, requires software updates and maintenance. Our cloud-hosted solution
                works on any device with a camera and benefits from rolling updates, ease of
                access and ensuring it stays reliable and accessible anywhere.{' '}
                <strong>
                  Communication is a right, not a privilege—explore our features below:
                </strong>
              </p>
            </div>

            <div className="why-cards-row">
              <div className="why-card why-card--secondary">
                <h3 className="why-card-title">Quick Chat</h3>
                <p className="why-card-body">
                  Pre-determined screen selection for guided conversation
                </p>
              </div>
              <div className="why-card why-card--secondary">
                <h3 className="why-card-title">Conversation Mode</h3>
                <p className="why-card-body">
                  Have full conversations using only your gaze with AI powered response to
                  natural spoken questions
                </p>
              </div>
            </div>

          </div>
        </section>

        {/* ── How It Works heading ──────────────────────────────────────── */}
        <div className="how-works-banner">
          <h2 className="display-heading">How Eye2Voice Works</h2>
        </div>

        {/* ── How It Works rows ─────────────────────────────────────────── */}
        <section className="section-how">

          {/* Row 1 */}
          <div className="how-row how-row--text-left">
            <div className="how-row-content">
              <h3 className="how-row-heading">Gaze Based Hands-Free Communication</h3>
              <p className="how-row-body">
                Our application uses four-way gaze to move through conversation blocks.
                Either using Quick Chat or Conversation Mode conversations are made seamless
                and accessible.
              </p>
              <a
                href="https://talk.eye2voice.com"
                className="btn-dark"
                target="_blank"
                rel="noopener noreferrer"
              >
                Try It Now
              </a>
            </div>
            <div className="how-row-image">
              <img
                src={`${process.env.PUBLIC_URL}/images/device-mockup.png`}
                alt="Eye2Voice device mockup"
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="how-row how-row--text-video">
            <div className="how-row-content">
              <h3 className="how-row-heading">AI Generated Response Prompts</h3>
              <p className="how-row-body">
                Listening Mode allows one user to speak or write in a response. An LLM
                generates realistic response options for another user to pick from using
                just gaze based navigation.
              </p>
              <Link to="/demo" className="btn-dark">Demo</Link>
            </div>
            <div className="how-row-image">
              <video
                src={`${process.env.PUBLIC_URL}/images/conversation-mode.mov`}
                autoPlay
                loop
                muted
                playsInline
                aria-label="Conversation Mode demo"
              />
            </div>
          </div>

        </section>

        {/* ── Application Features ──────────────────────────────────────── */}
        <section className="section-features">
          <h2 className="display-heading">Application Features</h2>

          {/* Feature 1 — wide card */}
          <div className="feature-card feature-card--wide">
            <div className="feature-card-image">
              <img
                src={`${process.env.PUBLIC_URL}/images/color-options.png`}
                alt="Color selection blocks showing multiple palette options"
              />
            </div>
            <div className="feature-card-content">
              <h3 className="feature-card-title">Custom Color Selection</h3>
              <p className="feature-card-body">
                Providing options to customize the text and background color of selection
                block. To support sight accessibility.
              </p>
            </div>
          </div>

          {/* Feature 2 + 3 — two cards row */}
          <div className="features-row">
            <div className="feature-card feature-card--small">
              <h3 className="feature-card-title">Read Aloud Mode</h3>
              <p className="feature-card-body">
                Announce each option aloud for individuals who cannot read.
              </p>
              <img
                src={`${process.env.PUBLIC_URL}/images/sound-icon.png`}
                alt="Sound speaker icon"
                className="feature-card-icon"
              />
            </div>
            <div className="feature-card feature-card--small">
              <h3 className="feature-card-title">Confirmation Behavior</h3>
              <img
                src={`${process.env.PUBLIC_URL}/images/blink-selection.png`}
                alt="Eye blink confirmation illustration"
                className="feature-card-icon feature-card-icon--round"
              />
              <p className="feature-card-body">
                Option to either use long gaze or double blink to confirm block selection.
              </p>
            </div>
          </div>
        </section>

        {/* ── Who Can Benefit ───────────────────────────────────────────── */}
        <section className="section-benefit">
          <div className="benefit-inner">
            <h2 className="display-heading">Who Can Benefit</h2>
            <div className="benefit-lists">

              <div className="benefit-list">
                <p className="benefit-list-label" style={{ color: 'var(--dark-green)' }}>
                  Individuals With
                </p>
                <ul>
                  <li>ALS / Motor Neuron Disease</li>
                  <li>Stroke or TBI recovery</li>
                  <li>Physical paralysis</li>
                  <li>Non-verbal or minimally verbal autism spectrum</li>
                </ul>
              </div>

              <div className="benefit-list">
                <p className="benefit-list-label" style={{ color: 'var(--label-brown)' }}>
                  Caregivers &amp; Clinicians
                </p>
                <ul>
                  <li>Family members &amp; home caregivers</li>
                  <li>Healthcare providers &amp; Clinicians</li>
                </ul>
              </div>

            </div>
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <section
          className="section-cta"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/cta-background.jpg)` }}
        >
          <div className="cta-overlay">
            <h2 className="cta-text">LOOK . DECIDE . SPEAK</h2>
          </div>
        </section>

      </main>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="home-footer">
        <p className="footer-text">
          UC Berkeley Masters of Information and Data Science Capstone Project
        </p>
        <a className="footer-email" href="mailto:eyetovoice@gmail.com">
          eyetovoice@gmail.com
        </a>
      </footer>
    </div>
  );
}
